import { debounce } from "../../utils";
class VerticalEnterpriseHeadshotDots {
    constructor(el, minDots, maxDots, minDistance, maxDistance, yOffsetMin, yOffsetMax, baseline = "wave", color = "#ffffff", topOffset = 0, limitTop = false) {
        this.el = el;
        this.width = this.el.clientWidth;
        this.prevWidth = this.width;
        this.height = this.el.clientHeight;
        this.canvasWrapper = this.el.querySelector('.line');
        this.canvas = document.createElement('canvas');
        this.dpr = window.devicePixelRatio * 2 || 2;
        this.superSampling = 2 * this.dpr;
        this.lineWidth = .7 * this.superSampling;
        this.canvas.width = this.width * this.dpr;
        this.canvas.height = this.height * this.dpr;
        this.el.classList.add('has-canvas');
        this.canvasWrapper.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        this.offscreenCanvas = document.createElement('canvas');
        this.offscreenCanvas.width = this.width * this.superSampling;
        this.offscreenCanvas.height = this.height * this.superSampling
        this.offscreenCtx = this.offscreenCanvas.getContext('2d');
        // this.offscreenCtx.scale(2, 2);
        // this.canvasWrapper.appendChild(this.offscreenCanvas);

        this.baseline = baseline;
        this.color = color;
        this.minDots = minDots;
        this.maxDots = maxDots;
        this.maxDistance = maxDistance;
        this.minDistance = minDistance;
        this.topOffset = topOffset;
        this.limitTop = limitTop;

        this.numDots = this.calculateNumDots();
        this.yOffsetMin = yOffsetMin;
        this.yOffsetMax = yOffsetMax;
        this.dots = [];
        this.connections = [];
        window.addEventListener('resize', debounce(() => {
            this.handleResize();
        }, 200));
        this.generateDots();
        this.connectDots();
        this.animate(true);
        this.isAnimating = false;

        this.el.addEventListener('mouseenter', () => {
            this.isAnimating = true;
        });

        this.el.addEventListener('mouseleave', () => {
            this.isAnimating = false;
        });
    }

    handleResize() {
        this.width = this.el.clientWidth;

        if (this.width === this.prevWidth) return;

        this.prevWidth = this.width;
        this.height = this.el.clientHeight;
        this.canvas.width = this.width * this.dpr;
        this.canvas.height = this.height * this.dpr;

        this.offscreenCanvas.width = this.width * this.superSampling;
        this.offscreenCanvas.height = this.height * this.superSampling;
        // this.offscreenCtx.scale(2, 2);

        this.dots = [];
        this.connections = [];

        this.numDots = this.calculateNumDots();
        this.generateDots();
        this.connectDots();
        this.animate(true);
        this.isAnimating = false;
    }

    calculateNumDots() {
        const screenWidth = window.innerWidth;
        const minWidth = 320; // minimum screen width
        const maxWidth = 1440; // maximum screen width

        if (screenWidth <= minWidth) {
            return this.minDots;
        } else if (screenWidth >= maxWidth) {
            return this.maxDots;
        } else {
            const t = (screenWidth - minWidth) / (maxWidth - minWidth);
            return Math.round(this.minDots + t * (this.maxDots - this.minDots));
        }
    }

    startAnimation() {
        if (!this.animationFrameID) {
            this.animate();
        }
    }

    stopAnimation() {
        if (this.animationFrameID) {
            cancelAnimationFrame(this.animationFrameID);
            this.animationFrameID = null;
        }
    }

    connectDots() {
        this.connections = [];
        this.dots.forEach((dot1, indexA) => {
            const availableDots = this.dots;
            let connectionCount = 0;

            availableDots.forEach((dot2, indexB) => {
                const distance = Math.sqrt(Math.pow(dot1.x - dot2.x, 2) + Math.pow(dot1.y - dot2.y, 2));
                const minDistance = this.minDistance * this.offscreenCanvas.height;
                const maxDistance = this.maxDistance * this.offscreenCanvas.height;
                if (distance >= minDistance && distance <= maxDistance) {
                    let alreadyConnected = false;
                    this.connections.forEach((connection) => {
                        if ((connection.dot1 === dot1 && connection.dot2 === dot2) || (connection.dot1 === dot2 && connection.dot2 === dot1)) {
                            alreadyConnected = true;
                        }
                    })
                    if (!alreadyConnected) {
                        this.connections.push({ dot1, dot2 });
                        connectionCount++;
                    }
                }
            });
            if(connectionCount === 0) {
                let findClosestDot = false;
                let tries = 0;
                while (!findClosestDot && tries < 20) {
                    let closestDot = null;
                    let closestDistance = 999999999;
                    availableDots.forEach((dot2, indexB) => {
                        const distance = Math.sqrt(Math.pow(dot1.x - dot2.x, 2) + Math.pow(dot1.y - dot2.y, 2));
                        if (distance < closestDistance) {
                            closestDistance = distance;
                            closestDot = dot2;
                        }
                    })
                    let alreadyConnected = false;
                    this.connections.forEach((connection) => {
                        if ((connection.dot1 === dot1 && connection.dot2 === closestDot) || (connection.dot1 === closestDot && connection.dot2 === dot1)) {
                            alreadyConnected = true;
                        }
                    })
                    if (!alreadyConnected) {
                        this.connections.push({dot1, dot2: closestDot});
                        findClosestDot = true;
                    }
                    tries++;
                }
            }

            if (connectionCount === 0 && indexA < this.dots.length - 1) {
                this.connections.push({ dot1, dot2: this.dots[indexA + 1] });
            }
        });
    }

    drawConnections() {
        this.connections.forEach((connection) => {
            this.drawLine(connection.dot1, connection.dot2);
        });
    }

    drawLine(dot1, dot2) {
        this.offscreenCtx.beginPath();
        this.offscreenCtx.moveTo(dot1.x, dot1.y);
        this.offscreenCtx.lineWidth = this.lineWidth;
        this.offscreenCtx.lineTo(dot2.x, dot2.y);

        this.offscreenCtx.shadowBlur = 2;
        this.offscreenCtx.shadowColor = this.color;

        this.offscreenCtx.stroke();
    }

    drawDots() {
        this.offscreenCtx.fillStyle = this.color;
        this.offscreenCtx.strokeStyle = this.color;
        this.dots.forEach((dot) => {
            this.offscreenCtx.beginPath();
            this.offscreenCtx.arc(dot.x, dot.y, dot.radius, 0, 2 * Math.PI);
            this.offscreenCtx.shadowBlur = 2;
            this.offscreenCtx.shadowColor = this.color;
            this.offscreenCtx.fill();
        });
    }

    generateDots() {
        for (let i = 0; i < this.numDots; i++) {
            const t = i / (this.numDots - 1);
            const x = t * this.offscreenCanvas.width * 1.3 - this.offscreenCanvas.width * 0.15;
            let angle = Math.PI / 2 + t * Math.PI;
            const yOffset = (Math.random() * (this.yOffsetMax - this.yOffsetMin) + this.yOffsetMin) * this.offscreenCanvas.height;
            const y = 0.5 * Math.cos(angle) * this.offscreenCanvas.height / 2 + this.offscreenCanvas.height / 2 + yOffset + this.topOffset * this.offscreenCanvas.height;
            const radius = (2 + 7 * Math.pow(Math.random(), 2)) * this.superSampling;
            const speedFactor = ((15 * this.superSampling - radius) / 20 * this.superSampling) / this.dpr;
            const speedX = speedFactor * (0.0002 + Math.random() * 0.0005);
            const speedY = speedFactor * (0.0002 + Math.random() * 0.0005);
            const accelerationX = speedFactor * (0.0002 + Math.random() * 0.0025);
            const accelerationY = speedFactor * (0.0002 + Math.random() * 0.0025);
            const angleOffset = Math.random() * 3 * Math.PI;
            const movementType = ['circle', 'ellipse', 'spiral', 'figureEight'][Math.floor(Math.random() * 4)];
            this.dots.push({ x, y, radius, speedX, accelerationX, accelerationY, speedY, yOffset, initialY: y, initialX: x, angleOffset, movementType });
        }
    }

    animate(once = false) {
        if(this.isAnimating || once) {
            this.offscreenCtx.clearRect(0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
            this.dots.forEach((dot) => {
                let movementX, movementY;

                switch (dot.movementType) {
                    case 'circle':
                        movementX = Math.sin(dot.speedX + dot.angleOffset) * 30;
                        movementY = Math.cos(dot.speedY + dot.angleOffset) * 30;
                        break;
                    case 'ellipse':
                        movementX = Math.sin(dot.speedX + dot.angleOffset) * 30;
                        movementY = Math.cos(dot.speedY * 2 + dot.angleOffset) * 15;
                        break;
                    case 'spiral':
                        movementX = Math.sin(dot.speedX + dot.angleOffset) * (30 - 0.1 * dot.speedX);
                        movementY = Math.cos(dot.speedY + dot.angleOffset) * (30 - 0.1 * dot.speedY);
                        break;
                    case 'figureEight':
                        movementX = Math.sin(dot.speedX + dot.angleOffset) * 30;
                        movementY = Math.cos(dot.speedX * 2 + dot.angleOffset) * 15;
                        break;
                }

                dot.x = dot.initialX + movementX;
                dot.y = dot.initialY + movementY;

                if(dot.limitTop && dot.y + dot.radius < 0) {
                    dot.y = dot.radius;
                }

                dot.speedY += dot.accelerationY;
                dot.speedX += dot.accelerationX;
            });
            this.drawDots();
            this.drawConnections();

            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            this.ctx.drawImage(this.offscreenCanvas, 0, 0, this.canvas.width, this.canvas.height);
        }

        this.animationFrameID = requestAnimationFrame(() => this.animate());
    }
}

export default VerticalEnterpriseHeadshotDots;
